import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { NotificationModel } from 'app/core/models/events/notification.model';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class NotificationSignalRService {
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private authService: AuthService,
  ) {
    this.authService.onLogout$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.disconnectAll();
      this.clear();
      this.isInitialized = false;
    });
  }

  private notificationsSubject = new BehaviorSubject<NotificationModel[]>([]);
  notifications$: Observable<NotificationModel[]> =
    this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();

  private hubs: Map<string, signalR.HubConnection> = new Map();

  private isInitialized = false;

  connectToHub(baseUrl: string, hubPath: string, userId: string) {
    if (!this.isInitialized) {
      this.initNotifications(userId);
      this.isInitialized = true;
    }

    const fullUrl = `${baseUrl}${hubPath}?userId=${encodeURIComponent(userId)}`;

    if (this.hubs.has(fullUrl)) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(fullUrl, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.on('NotificationChanged', (message: NotificationModel) => {
      const includesIds = message.targetUserIds || [];
      if (includesIds.includes(userId)) {
        this.handleIncomingNotification(message);
      } else {
      }
    });

    connection
      .start()
      .then(() => {
        console.log(`Connected to: ${hubPath}`);
        this.hubs.set(fullUrl, connection);
      })
      .catch((err) => console.error(`Error connecting to ${hubPath}:`, err));
  }

  private handleIncomingNotification(message: any) {
    this.ngZone.run(() => {
      const current = this.notificationsSubject.value;

      const mapped = this.mapToModel(message);

      const isDuplicate = mapped.id && current.some((n) => n.id === mapped.id);

      if (isDuplicate) {
        return;
      }

      const updated = [mapped, ...current].slice(0, 10);

      this.notificationsSubject.next(updated);

      this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
    });
  }

  disconnectAll() {
    this.hubs.forEach((conn) => conn.stop());
    this.hubs.clear();
  }

  markAllAsRead() {
    this.unreadCountSubject.next(0);
  }

  clear() {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  removeFromList(notificationId: string) {
    const current = this.notificationsSubject.value;

    const updated = current.filter((n) => n.id !== notificationId);

    this.notificationsSubject.next(updated);

    const unreadCount = updated.filter((n) => !n.isRead).length;
    this.unreadCountSubject.next(unreadCount);
  }

  initNotifications(userId: string) {
    this.notificationService
      .getUnreadNotificationsByUserId(userId)
      .subscribe((res) => {
        this.ngZone.run(() => {
          if (res.isSuccess && res.data) {
            const mappedNotifications = res.data.map((item) =>
              this.mapToModel(item),
            );

            this.notificationsSubject.next(mappedNotifications);
            this.unreadCountSubject.next(mappedNotifications.length);
          }
        });
      });
  }

  private mapToModel(item: any): NotificationModel {
    return {
      id: item.id || '',
      title: item.title || '',
      content: item.content || '',
      targetUrl: item.targetUrl || '',
      senderUserName: item.senderUserName || '',
      payload:
        typeof item.payload === 'string'
          ? item.payload
          : JSON.stringify(item.payload || {}),
      eventName: item.eventName || '',
      isRead: item.isRead || false,
      receiverUserId: item.receiverUserId || '',
      targetUserIds: item.targetUserIds,
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
